import { useMemo } from 'react'
import { Breadcrumbs, Heading, Image, Link, Text } from '../../Atoms'
import { Grid, Markdowned } from '../../Molecules'
import { LeksikonDocumentProps } from './props'

const highlightTerms = (searchTerm: string, content: string) => {
    let res = content
    if (searchTerm && searchTerm !== '') {
        searchTerm.split(' ').forEach((term) => {
            const regex = new RegExp(`${term}`, 'g')
            res = res.replace(regex, `<span id='${term}'>${term}</span>`)
        })
    }
    return res
}

const mapGoogleURL = (src: string) => {
    const params = src.split('/')
    const imageId = params[params.length - 2]
    return `https://drive.google.com/uc?id=${imageId}`
}

const mapURL = (src?: string) => {
    if (!src) return ''
    const isGoogleDrive = src.includes('drive.google.com')
    return isGoogleDrive ? mapGoogleURL(src) : src
}

const p = ({ children, ...props }: any) => <Text {...props}>{children}</Text>

const span = ({ children, ...props }: any): JSX.Element => (
    <Text
        css={{
            bgc: '$secondary400',
            br: '$1',
        }}
        {...props}
    >
        {children}
    </Text>
)

const a = ({ children, ...props }: any) => {
    const isExternal = props.href?.includes('partner.raskrask.dk')
    const target = isExternal ? '_self' : '_blank'

    return (
        <Link target={target} {...props}>
            {children}
        </Link>
    )
}

const getHeading = (level: number, props: any) => (
    <Heading
        bold
        css={{
            '&:not(:first-child)': {
                m: '$3 0 $1',
            },
        }}
        level={level}
        size={5}
        {...props}
    />
)

const ol = ({ children, ...props }: any) => (
    <ol style={{ listStyle: 'decimal', margin: 0, paddingLeft: 24 }} {...props}>
        {children}
    </ol>
)

const ul = ({ children, ...props }: any) => (
    <ul style={{ listStyle: 'disc', margin: 0, paddingLeft: 24 }} {...props}>
        {children}
    </ul>
)

const iframe = ({ children, ...props }: any) => (
    <iframe
        title="video"
        style={{
            width: '100%',
            height: '360px',
            border: 'none',
            overflow: 'hidden',
        }}
        {...props}
    >
        {children}
    </iframe>
)

const image = (props: any) => <Image src={mapURL(props.src)} />

const LeksikonDocument = ({
    breadcrumbs,
    content,
    highLightedText,
    hideHeader,
    title,
}: LeksikonDocumentProps) => {
    const contentHighLighted = useMemo(
        () => highlightTerms(highLightedText ?? '', content),
        [content, highLightedText]
    )

    return (
        <Grid gap={16}>
            <Breadcrumbs links={breadcrumbs} />
            {!hideHeader && (
                <Heading level={1} size={4} primary>
                    {title}
                </Heading>
            )}
            <Markdowned
                options={{
                    wrapper: (props) => <Grid {...props} />,
                    overrides: {
                        h1: {
                            component: (props) => getHeading(1, props),
                        },
                        h2: {
                            component: (props) => getHeading(2, props),
                        },
                        h3: {
                            component: (props) => getHeading(3, props),
                        },
                        h4: {
                            component: (props) => getHeading(4, props),
                        },
                        p: { component: p },
                        span: { component: span },
                        a: { component: a },
                        ol: { component: ol },
                        ul: { component: ul },
                        iframe: { component: iframe },
                        img: { component: image },
                    },
                }}
                text={contentHighLighted}
            />
        </Grid>
    )
}

export default LeksikonDocument
